import { portalFetch } from '../../shared/auth/portalSession';
import { API_BASE_URL } from './config';
import { Service } from '../types';
import { getAuthHeaders, requireAuthentication } from './authUtils';

export interface ServiceApiPayload {
  service_name: string;
  supported_days: string[];
  supported_providers: string[];
  is_active: boolean;
}

export interface ServiceApiResponse {
  service_id: string;
  service_name: string;
  supported_days: string[];
  supported_providers: string[];
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Helper to convert API response to frontend Service type
function convertApiToService(apiService: ServiceApiResponse): Service {
  return {
    id: apiService.service_id,
    name: apiService.service_name,
    availableDays: apiService.supported_days,
    availableProviders: apiService.supported_providers as any[],
  };
}

// Helper to convert frontend Service to API payload
function convertServiceToApi(service: Omit<Service, 'id'>): ServiceApiPayload {
  return {
    service_name: service.name,
    supported_days: service.availableDays,
    supported_providers: service.availableProviders,
    is_active: true,
  };
}

export async function getServicesApi(): Promise<Service[]> {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/services`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch services: ${response.statusText}`);
    }

    const data: { services: ServiceApiResponse[]; count: number } = await response.json();
    return data.services.map(convertApiToService);
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
}

export async function getServiceByIdApi(id: string): Promise<Service> {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/services/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch service: ${response.statusText}`);
    }

    const data = await response.json();
    // Handle both wrapped and unwrapped responses
    const serviceData = data.service || data;
    return convertApiToService(serviceData);
  } catch (error) {
    console.error('Error fetching service by ID:', error);
    throw error;
  }
}

export async function addServiceApi(service: Omit<Service, 'id'>): Promise<Service> {
  requireAuthentication();
  
  try {
    const payload = convertServiceToApi(service);
    
    const response = await portalFetch('admin', `${API_BASE_URL}/services`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to add service: ${response.statusText}`);
    }

    const data: ServiceApiResponse = await response.json();
    return convertApiToService(data);
  } catch (error) {
    console.error('Error adding service:', error);
    throw error;
  }
}

export async function updateServiceApi(id: string, service: Omit<Service, 'id'>): Promise<Service> {
  requireAuthentication();
  
  try {
    const payload = convertServiceToApi(service);
    
    const response = await portalFetch('admin', `${API_BASE_URL}/services/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to update service: ${response.statusText}`);
    }

    const data: ServiceApiResponse = await response.json();
    return convertApiToService(data);
  } catch (error) {
    console.error('Error updating service:', error);
    throw error;
  }
}

export async function deleteServiceApi(id: string): Promise<void> {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/services/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const error: any = new Error(errorData.message || `Failed to delete service: ${response.statusText}`);
      error.response = { status: response.status, data: errorData };
      throw error;
    }
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
}

export async function checkServiceAssociationsApi(id: string): Promise<string[]> {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/services/${id}/associations`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to check service associations: ${response.statusText}`);
    }

    const data = await response.json();
    return data.associatedTechnicians || [];
  } catch (error) {
    console.error('Error checking service associations:', error);
    throw error;
  }
}
